import { useState } from 'react';
import { Tabs } from 'antd';
import { faRotateRight, faGear } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from './auth/AuthContext';
import { LocationProvider } from './auth/LocationContext';
import { LocationPicker } from './components/LocationPicker';
import { ProjectProvider } from './auth/ProjectContext';
import { ProjectPicker } from './components/ProjectPicker';
import { Loader } from './components/Loader';
import ActionButton from './components/ui/ActionButton';
import { PageActionsProvider, usePageActions } from './components/PageActions';
import { ReceiptsPendingTab } from './pages/ReceiptsPendingTab';
import { DepositsReportTab } from './pages/DepositsReportTab';
import { RegisterCloseTab } from './pages/RegisterCloseTab';
import { SafeTab } from './pages/SafeTab';
import { PettyCashTab } from './pages/PettyCashTab';
import { CreditTab } from './pages/CreditTab';
import { DepositPdfDrawer } from './features/deposits/DepositPdfDrawer';
import { SettingsDrawer } from './features/cash/SettingsDrawer';
import { useBoxSettings } from './hooks/useBoxSettings';

type Tab = 'pending' | 'report' | 'close' | 'closings' | 'petty' | 'safe' | 'deposits' | 'credit';

function AppHeaderRefresh() {
  const { refresh } = usePageActions();
  if (!refresh) return null;
  return <ActionButton icon={faRotateRight} label="רענן" onClick={refresh} />;
}

export default function App() {
  const auth = useAuth();
  const { data: settings } = useBoxSettings(auth.status === 'authenticated');
  const [tab, setTab] = useState<Tab>('pending');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const registerMode = !!settings?.handles_cash && settings?.cash_mode === 'register';
  // The safe side (כספת + הפקדות לבנק) is restricted to high-role Arbox users.
  // The server enforces this on /api/bank-deposits; here we just hide the tabs.
  const showSafeTabs = registerMode && auth.canViewSafe;
  // The receipt-based tabs (תקבולים להפקדה + דוח הפקדות) are only useful when
  // something can land in them: checks (always flow there) or cash deposited
  // directly. A cash-only box in register mode has neither — its whole flow is
  // register → safe — so those tabs are hidden. Default to showing them while
  // settings are still loading.
  const showReceiptTabs = settings
    ? settings.handles_checks || (settings.handles_cash && settings.cash_mode === 'direct')
    : true;
  // In register mode the receipt tabs are checks-only, so label them as such.
  const pendingLabel = registerMode ? "צ'קים להפקדה" : 'תקבולים להפקדה';
  const reportLabel = registerMode ? "הפקדות צ'קים" : 'דוח הפקדות';
  // Petty cash is loaded from cash receipts, so it sits right after the steps
  // that hold free cash (receipts / register). Open to every user, no role gate.
  const showPettyCash = !!settings?.petty_cash_enabled;
  // Credit card deposits are their own flow (installments → company → bank).
  const showCredit = !!settings?.credit_enabled;
  const visibleKeys: Tab[] = [
    ...(showReceiptTabs ? (['pending'] as Tab[]) : []),
    ...(registerMode ? (['close', 'closings'] as Tab[]) : []),
    ...(showPettyCash ? (['petty'] as Tab[]) : []),
    ...(showSafeTabs ? (['safe', 'deposits'] as Tab[]) : []),
    ...(showReceiptTabs ? (['report'] as Tab[]) : []),
    ...(showCredit ? (['credit'] as Tab[]) : []),
  ];
  // Keep the active key pointed at a tab that's actually rendered.
  const activeTab = visibleKeys.includes(tab) ? tab : visibleKeys[0] ?? 'pending';
  // Lifted so both Tab A (create-success) and Tab B (פירוט / הדפסה) open the
  // same in-app PDF drawer.
  const [pdfDepositId, setPdfDepositId] = useState<number | null>(null);

  if (auth.status === 'loading') return <Loader fullScreen />;

  if (auth.status === 'unauthenticated' || auth.status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-grey-bg p-8">
        <div className="text-center">
          <div
            className="text-main-text mb-2 font-semibold"
            style={{ fontSize: '1.625rem' }}
          >
            לא מחובר
          </div>
          <div className="text-small text-secondary-text">{auth.error}</div>
        </div>
      </div>
    );
  }

  return (
    <LocationProvider>
      <ProjectProvider>
      <PageActionsProvider>
        <div className="min-h-screen flex flex-col bg-grey-bg">
          <main className="flex-1 min-h-0 flex flex-col px-4 lg:px-8 pt-3 lg:pt-5 pb-6">
            <div className="flex items-center justify-between gap-2 sticky top-0 z-20 -mx-4 px-4 py-2 bg-grey-bg sm:static sm:mx-0 sm:px-0 sm:py-0 sm:bg-transparent">
              <div className="flex items-baseline gap-3">
                <h1
                  className="text-xl sm:text-[1.625rem] font-semibold text-main-text"
                  dir="auto"
                >
                  הפקדות
                </h1>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <LocationPicker />
                <ProjectPicker />
                <AppHeaderRefresh />
                <ActionButton icon={faGear} label="הגדרות" onClick={() => setSettingsOpen(true)} />
              </div>
            </div>

            <Tabs
              activeKey={activeTab}
              onChange={(k) => setTab(k as Tab)}
              destroyInactiveTabPane
              className="deposits-tabs mt-3 sm:mt-4 flex-1 min-h-0"
              // Tab order follows the money. In register mode the cash chain is
              // receipts → קופה → סגירות קופה → כספת → הפקדות לבנק → deposit, so
              // each step and its history sit in flow order between the receipts
              // screen and the (receipt-based, mostly checks) deposits report. In
              // direct mode there's just receipts → report.
              items={[
                ...(showReceiptTabs
                  ? [
                      {
                        key: 'pending',
                        label: pendingLabel,
                        children: (
                          <ReceiptsPendingTab
                            onDepositCreated={(id) => {
                              setTab('report');
                              setPdfDepositId(id);
                            }}
                          />
                        ),
                      },
                    ]
                  : []),
                ...(registerMode
                  ? [
                      { key: 'close', label: 'סגירת קופה', children: <RegisterCloseTab view="register" /> },
                      { key: 'closings', label: 'סגירות קופה', children: <RegisterCloseTab view="closings" /> },
                    ]
                  : []),
                ...(showPettyCash
                  ? [{ key: 'petty', label: 'קופה קטנה', children: <PettyCashTab /> }]
                  : []),
                ...(showSafeTabs
                  ? [
                      { key: 'safe', label: 'כספת', children: <SafeTab view="safe" /> },
                      { key: 'deposits', label: 'הפקדות לבנק', children: <SafeTab view="deposits" /> },
                    ]
                  : []),
                ...(showReceiptTabs
                  ? [
                      {
                        key: 'report',
                        label: reportLabel,
                        children: <DepositsReportTab onOpenPdf={(id) => setPdfDepositId(id)} />,
                      },
                    ]
                  : []),
                ...(showCredit
                  ? [{ key: 'credit', label: 'הפקדות אשראי', children: <CreditTab /> }]
                  : []),
              ]}
            />
          </main>

          <DepositPdfDrawer
            open={pdfDepositId !== null}
            depositId={pdfDepositId}
            onClose={() => setPdfDepositId(null)}
          />

          <SettingsDrawer open={settingsOpen} onClose={() => setSettingsOpen(false)} />
        </div>
      </PageActionsProvider>
      </ProjectProvider>
    </LocationProvider>
  );
}
